import React, { useState, useRef, useEffect } from 'react';
import { scaleLinear } from 'd3-scale';
import { line, curveMonotoneX } from 'd3-shape';
import { brushX } from 'd3-brush';
import { select } from 'd3-selection';

type Primitive = string | number;

export interface D3LineChartDatum {
  key: Primitive;
  value: number;
}

interface D3LineChartProps {
  data: D3LineChartDatum[];
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  title?: string;
  xLabel?: string;
  yLabel?: string;
  formatXTick?: (k: Primitive) => string;
  formatYTick?: (n: number) => string;
  onBrush?: (range: [number, number] | null) => void;
  brushRange?: [number, number] | null;
  colorScaleFunc?: (value: number) => string;
}

interface TooltipData {
  key: Primitive;
  value: number;
  x: number;
  y: number;
}

export const D3LineChart: React.FC<D3LineChartProps> = ({
  data,
  width: propWidth,
  height: propHeight = 320,
  margin: propMargin,
  title,
  xLabel,
  yLabel,
  formatXTick = (k) => String(k),
  formatYTick = (n) => String(n),
  onBrush,
  brushRange,
  colorScaleFunc,
}) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const brushRef = useRef<SVGGElement>(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const [isMobile, setIsMobile] = useState(false);
  const isProgrammaticUpdate = useRef(false);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const paddingLeft = parseFloat(computedStyle.paddingLeft);
        const paddingRight = parseFloat(computedStyle.paddingRight);
        const width = containerRef.current.offsetWidth - paddingLeft - paddingRight;
        setContainerWidth(width);
        setIsMobile(width < 500);
      }
    };
    
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);
  
  const margin = propMargin || (isMobile 
    ? { top: 12, right: 12, bottom: 32, left: 58 }
    : { top: 16, right: 24, bottom: 36, left: 60 }
  );

  const width = propWidth || containerWidth;
  const height = propHeight;

  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const maxValue = data?.length ? Math.max(...data.map((d) => Number(d.value) || 0), 1) : 1;
  const minKey = data?.length ? Math.min(...data.map((d) => Number(d.key))) : 0;
  const maxKey = data?.length ? Math.max(...data.map((d) => Number(d.key))) : 1;

  const xScale = scaleLinear()
    .domain([minKey, maxKey])
    .range([0, innerW]);

  const yScale = scaleLinear()
    .domain([0, maxValue])
    .range([innerH, 0])
    .nice();

  const lineGenerator = line<D3LineChartDatum>()
    .x((d: D3LineChartDatum) => xScale(Number(d.key)))
    .y((d: D3LineChartDatum) => yScale(Number(d.value)))
    .curve(curveMonotoneX);

  const pathData = data?.length ? lineGenerator(data) || '' : '';
  const xTickCount = isMobile ? Math.min(6, Math.floor(innerW / 40)) : Math.min(10, data?.length || 0);
  const xTicks = xScale.ticks(xTickCount);
  
  const fontSize = isMobile ? 10 : 12;
  const titleFontSize = isMobile ? 12 : 14;
  const tickCount = isMobile ? 4 : 6;

  useEffect(() => {
    if (!brushRef.current || !onBrush || !data?.length) return;

    const brush = brushX()
      .extent([[0, 0], [innerW, innerH]])
      .on('end', (event) => {
        if (isProgrammaticUpdate.current) {
          isProgrammaticUpdate.current = false;
          return;
        }
        if (!event.selection) {
          onBrush(null);
          return;
        }
        const [x0, x1] = event.selection as [number, number];
        const range: [number, number] = [xScale.invert(x0), xScale.invert(x1)];
        onBrush(range);
      });

    const brushGroup = select(brushRef.current);
    brushGroup.call(brush);

    isProgrammaticUpdate.current = true;
    if (brushRange) {
      const [min, max] = brushRange;
      brush.move(brushGroup, [xScale(min), xScale(max)]);
    } else {
      brush.move(brushGroup, null);
    }
  }, [innerW, innerH, xScale, onBrush, brushRange, data]);
  
  if (!data?.length) return null;

  return (
    <div className="line-chart-container">
      <style>{`
        .brush .selection {
          fill: rgba(41, 121, 255, 0.2);
          stroke: #2979FF;
          stroke-width: 1px;
        }
        .brush .handle {
          fill: #2979FF;
        }
      `}</style>
      {title ? <h3>{title}</h3> : null}
      <div ref={containerRef} style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
        <svg width={width} height={height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          <path
            d={pathData}
            fill="none"
            stroke="#4a5568"
            strokeWidth={2.5}
            style={{ transition: 'd 400ms' }}
          />

          {data.map((d) => {
            const cx = xScale(Number(d.key));
            const cy = yScale(Number(d.value) || 0);
            const fillColor = colorScaleFunc ? colorScaleFunc(Number(d.value)) : '#2979FF';
            return (
              <circle
                key={String(d.key)}
                cx={cx}
                cy={cy}
                r={4}
                fill={fillColor}
                stroke="#1e293b"
                strokeWidth={1.5}
                style={{ 
                  cursor: 'pointer', 
                  transition: 'cx 400ms, cy 400ms, filter 200ms, transform 200ms',
                  transformOrigin: `${cx}px ${cy}px`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.2)';
                  e.currentTarget.style.transform = 'scale(1.5)';
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    key: d.key,
                    value: Number(d.value),
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                  e.currentTarget.style.transform = 'scale(1)';
                  setTooltip(null);
                }}
              />
            );
          })}

          {/* X axis */}
          <g transform={`translate(0, ${innerH})`}>
            <line x1={0} x2={innerW} stroke="#4a5568" strokeWidth={1} />
            {xTicks.map((tick) => {
              const cx = xScale(tick);
              return (
                <g key={`xtick-${tick}`} transform={`translate(${cx},0)`}>
                  <line y1={0} y2={6} stroke="#4a5568" />
                  <text
                    y={18}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fill="#a0aec0"
                    fontWeight={500}
                  >
                    {formatXTick(tick)}
                  </text>
                </g>
              );
            })}
            {xLabel ? (
              <text
                x={innerW / 2}
                y={isMobile ? 30 : 34}
                textAnchor="middle"
                fontSize={titleFontSize}
                fill="#e2e8f0"
                fontWeight={600}
              >
                {xLabel}
              </text>
            ) : null}
          </g>

          {/* Y axis */}
          <g>
            <line y1={0} y2={innerH} stroke="#4a5568" strokeWidth={1} />
            {yScale.ticks(tickCount).map((tick) => (
              <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
                <line x2={-6} stroke="#4a5568" />
                <text x={-10} textAnchor="end" dy="0.35em" fontSize={fontSize} fill="#a0aec0" fontWeight={500}>
                  {formatYTick(tick)}
                </text>
              </g>
            ))}
            {yLabel ? (
              <text
                x={isMobile ? -45 : -50}
                y={innerH / 2}
                textAnchor="middle"
                fontSize={titleFontSize}
                fill="#e2e8f0"
                fontWeight={600}
                transform={`rotate(-90, ${isMobile ? -45 : -50}, ${innerH / 2})`}
              >
                {yLabel}
              </text>
            ) : null}
          </g>

          {onBrush && <g ref={brushRef} className="brush" />}
        </g>
      </svg>
      </div>
      
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(30, 41, 59, 0.95)',
            color: '#e2e8f0',
            padding: isMobile ? '6px 10px' : '8px 12px',
            borderRadius: '6px',
            fontSize: isMobile ? '11px' : '13px',
            fontWeight: 500,
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            lineHeight: '1.5',
          }}
        >
          <div>{xLabel || 'Categoria'}: {formatXTick(tooltip.key)}</div>
          <div>{yLabel || 'Valor'}: {formatYTick(tooltip.value)}</div>
        </div>
      )}
    </div>
  );
};

